import React from 'react';

import type {
    ReportWithMappingDto,
    ReportWithMappingFlattened,
} from '@klp/reportMapping/types';
import i18n from 'i18next';
import { MappingActions } from '../MappingActions';
import type { Theme } from '@material-ui/core';
import type { MappingTableData } from '@klp/reportMapping/hooks/useMappingTableData';
import type { MUIDataTableOptions } from 'mui-datatables';

export const getStyles = () => {
    const styles = (theme: Theme) => ({
        filterContainer: {
            marginBottom: theme.spacing(3),
            padding: theme.spacing(2),
            backgroundColor: theme.palette.grey[50],
            border: `1px solid ${theme.palette.grey[300]}`,
        },
        table: {
            '& thead': {
                backgroundColor: theme.palette.secondary.light,
                '& tr th': {
                    borderLeft: `1px dotted ${theme.palette.grey[200]}`,
                    backgroundColor: theme.palette.secondary.light,
                    '&:first-child': {
                        borderLeft: 'none',
                    },
                },
            },
            '& tbody': {
                '& tr': {
                    '&:nth-of-type(odd)': {
                        backgroundColor: theme.palette.grey[50],
                    },
                    '& td': {
                        borderLeft: `1px dotted ${theme.palette.grey[200]}`,
                        '&:first-child': {
                            borderLeft: 'none',
                        },
                        '&:nth-child(5)': {
                            textAlign: 'center',
                            '& > div > div': {
                                fontSize: '0.875em',
                            },
                        },
                    },
                },
            },
        },
        tableBlurred: {
            '& tbody tr': {
                opacity: 0.4,
                filter: 'blur(1px)',
                transition: 'opacity 0.2s, filter 0.2s',
                pointerEvents: 'none',
            },
            '& tbody tr[data-active="true"]': {
                opacity: 1,
                filter: 'none',
                pointerEvents: 'auto',
                backgroundColor: theme.palette.secondary.veryLight,
            },
        },
    });
    return styles;
};

export const getTableColumns = (props: {
    filteredDataMap: Map<string, ReportWithMappingFlattened>;
    openPopperId: string | null;
    anchorEl: HTMLElement | null;
    handleTogglePopper: (
        reportId: string,
    ) => (event: React.MouseEvent<HTMLButtonElement>) => void;
    handleClosePopper: () => void;
}) => {
    const {
        filteredDataMap,
        openPopperId,
        anchorEl,
        handleTogglePopper,
        handleClosePopper,
    } = props;
    return [
        {
            name: 'isMapped' as const,
            options: {
                filter: false,
                display: 'excluded' as const,
            },
        },
        {
            label: 'ID',
            name: 'id' as const,
            options: {
                filter: false,
                sort: true,
            },
        },
        {
            label: i18n.t('reportMapping.mappingTable.type'),
            name: 'type' as const,
            options: {
                filter: true,
                sort: true,
                filterOptions: {
                    fullWidth: true,
                },
                customFilterListOptions: {
                    render: (v) =>
                        `${i18n.t('reportMapping.mappingTable.type')}: ${v}`,
                },
            },
        },
        {
            label: i18n.t('reportMapping.mappingTable.report'),
            name: 'name' as const,
            options: {
                filter: false,
                sort: true,
            },
        },
        {
            label: i18n.t('reportMapping.mappingTable.building'),
            name: 'byggNavn' as const,
            options: {
                filter: true,
                sort: true,
                filterOptions: {
                    fullWidth: true,
                    renderValue: (v) => v || '(empty)',
                },
                customFilterListOptions: {
                    render: (v) =>
                        `${i18n.t('reportMapping.mappingTable.building')}: ${v || '(empty)'}`,
                },
                customBodyRender: (value) => {
                    if (!value || !Array.isArray(value) || value.length === 0) {
                        return;
                    }
                    return (
                        <div>
                            {value.map((name, index) => (
                                <div key={index}>{name}</div>
                            ))}
                        </div>
                    );
                },
            },
        },
        {
            label: i18n.t('reportMapping.mappingTable.customer'),
            name: 'kundeNavn' as const,
            options: {
                filter: true,
                sort: true,
                filterOptions: {
                    fullWidth: true,
                    renderValue: (v) => v || '(empty)',
                },
                customFilterListOptions: {
                    render: (v) =>
                        `${i18n.t('reportMapping.mappingTable.customer')}: ${v || '(empty)'}`,
                },
            },
        },
        {
            label: i18n.t('reportMapping.mappingTable.actions'),
            name: 'actions' as const,
            options: {
                filter: false,
                sort: false,
                viewColumns: false,
                customBodyRender: (_value, tableMeta) => {
                    const reportId = tableMeta.rowData[1];
                    const originalItem = filteredDataMap.get(reportId);
                    const mappingData = originalItem?.mapping;
                    const preLoadData: ReportWithMappingDto = {
                        isMapped: !!mappingData,
                        mapping: mappingData ?? null,
                        id: originalItem?.id ?? '',
                        name: originalItem?.name ?? null,
                        title: originalItem?.title ?? '',
                        type: originalItem?.type ?? '',
                        description: originalItem?.description ?? '',
                    };
                    return (
                        <MappingActions
                            reportId={reportId}
                            isMapped={tableMeta.rowData[0]}
                            mappingData={preLoadData}
                            reportName={`${tableMeta.rowData[2]} / ${tableMeta.rowData[3]}`}
                            openPopperId={openPopperId}
                            anchorEl={anchorEl}
                            onTogglePopper={handleTogglePopper}
                            onClosePopper={handleClosePopper}
                        />
                    );
                },
            },
        },
    ];
};

export const getTableOptions = (props: {
    openPopperId: string | null;
    flatList: Array<MappingTableData>;
}): MUIDataTableOptions => {
    const { openPopperId, flatList } = props;
    return {
        search: true,
        filter: true,
        download: false,
        print: false,
        storageKey: 'reportMappingTable',
        viewColumns: true,
        selectableRows: 'none' as const,
        expandableRows: false,
        expandableRowsHeader: false,
        enableNestedDataAccess: '.',
        textLabels: {
            body: {
                noMatch: i18n.t('tables.body.noMatch'),
                toolTip: i18n.t('tables.body.toolTip'),
                columnHeaderTooltip: (column) =>
                    i18n.t('tables.body.toolTipFor') + column.label,
            },
            pagination: {
                next: i18n.t('tables.pagination.next'),
                previous: i18n.t('tables.pagination.previous'),
                rowsPerPage: i18n.t('tables.pagination.rowsPerPage'),
                displayRows: i18n.t('tables.pagination.displayRows'),
            },
            filter: {
                all: i18n.t('tables.filter.all'),
                title: i18n.t('tables.filter.title'),
                reset: i18n.t('tables.filter.reset'),
            },
            toolbar: {
                search: i18n.t('tables.toolbar.search'),
                downloadCsv: i18n.t('tables.toolbar.downloadCsv'),
                print: i18n.t('tables.toolbar.print'),
                viewColumns: i18n.t('tables.toolbar.viewColumns'),
                filterTable: i18n.t('tables.toolbar.filterTable'),
            },
            viewColumns: {
                title: i18n.t('tables.viewColumns.title'),
                titleAria: i18n.t('tables.viewColumns.titleAria'),
            },
        },
        setRowProps: (row, dataIndex) => {
            const reportId = flatList[dataIndex]?.id;
            return {
                'data-active': openPopperId === reportId ? 'true' : 'false',
            };
        },
        setFilterChipProps: () => {
            return {
                color: 'secondary' as const,
                variant: 'outlined' as const,
            };
        },
    };
};
